/**
 * Where the builder key lives on this browser (architecture §12, §14.78): `localStorage` when the
 * owner keeps the "Keep me signed in on this browser" box checked, `sessionStorage` (this tab only)
 * when they uncheck it. Pure functions over injected storages, so the rules are tested without a
 * DOM; `tokens.ts#getAccountKey`/`setAccountKey` bind them to the real storages, and the query
 * cache's owner check (`liveQuery.ts`) reads through `readAccountKey` too — one reader, either
 * storage. Only the builder key moves between storages; device tokens stay in `localStorage`
 * because the runtime SDK reads them there (§10).
 */
import { STORAGE_KEYS } from '@circles/convex';

/** The slice of `Storage` these rules need; tests pass a plain in-memory fake. */
export type KeyStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The key, whichever storage holds it (a kept key in `localStorage`, a tab-only one in `sessionStorage`). */
export function readAccountKey(local: KeyStorage | null, session: KeyStorage | null): string | null {
  return local?.getItem(STORAGE_KEYS.accountKey) ?? session?.getItem(STORAGE_KEYS.accountKey) ?? null;
}

/**
 * Stores the key where the checkbox says: kept → `localStorage`, not kept → `sessionStorage` only.
 * The other storage is cleared, so exactly one holds the key and a later read is unambiguous.
 */
export function writeAccountKey(local: KeyStorage | null, session: KeyStorage | null, accountKey: string, keep: boolean): void {
  (keep ? local : session)?.setItem(STORAGE_KEYS.accountKey, accountKey);
  (keep ? session : local)?.removeItem(STORAGE_KEYS.accountKey);
}

/** Sign-out ("Forget this key") clears both storages, whatever the checkbox was. */
export function clearAccountKey(local: KeyStorage | null, session: KeyStorage | null): void {
  local?.removeItem(STORAGE_KEYS.accountKey);
  session?.removeItem(STORAGE_KEYS.accountKey);
}

/**
 * Where the query cache (`circles.cache.v1.*`, liveQuery.ts) persists: `sessionStorage` while the
 * builder key is session-only — cached builder results must not outlive a tab-only sign-in — and
 * `localStorage` otherwise (kept key, or none: a member's launcher cache works as before). Decided
 * per operation, so a session-only sign-in moves the cache without a reload; the sign-in itself
 * clears the `localStorage` entries first (`setAccountKey` clears before it writes the key, while
 * the choice is still `local`).
 */
export function cacheStorageChoice(local: KeyStorage | null, session: KeyStorage | null): 'local' | 'session' {
  const kept = local?.getItem(STORAGE_KEYS.accountKey) != null;
  const tabOnly = session?.getItem(STORAGE_KEYS.accountKey) != null;
  return !kept && tabOnly ? 'session' : 'local';
}

/** `localStorage`, or null where storage is unavailable (private modes, storage disabled). */
export function browserLocalStorage(): KeyStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** `sessionStorage`, or null where storage is unavailable. */
export function browserSessionStorage(): KeyStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}
